import React, { useEffect, useState } from 'react';

export const InAppBrowserBypass: React.FC = () => {
  const [showBypass, setShowBypass] = useState(false);
  const [sourceApp, setSourceApp] = useState<'instagram' | 'tiktok' | 'facebook' | 'generic'>('generic');
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = (navigator.userAgent || navigator.vendor || (window as any).opera || '').toLowerCase();
    
    // Detect OS
    const ios = /iphone|ipad|ipod/.test(ua);
    setIsIOS(ios);

    // Detect In-App Browser Source
    const isInsta = ua.includes('instagram');
    const isTT = ua.includes('tiktok') || ua.includes('musical_ly');
    const isFB = ua.includes('fban') || ua.includes('fbav');

    if (isInsta) {
      setSourceApp('instagram');
      setShowBypass(true);
    } else if (isTT) {
      setSourceApp('tiktok');
      setShowBypass(true);
    } else if (isFB) {
      setSourceApp('facebook');
      setShowBypass(true);
    }
  }, []);

  if (!showBypass) return null;

  const getAppIcon = () => {
    switch (sourceApp) {
      case 'instagram':
        return (
          <div className="size-14 rounded-2xl bg-gradient-to-tr from-[#f9ce71] via-[#ee2a7b] to-[#6228d7] flex items-center justify-center text-white shadow-[0_0_25px_rgba(238,42,123,0.3)]">
            <span className="material-symbols-outlined text-3xl font-black">photo_camera</span>
          </div>
        );
      case 'tiktok':
        return (
          <div className="size-14 rounded-2xl bg-[#010101] border border-white/20 flex items-center justify-center text-white shadow-[0_0_25px_rgba(0,0,0,0.8)] relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#25f4ee_0%,_#fe2c55_100%)] opacity-30"></div>
            <span className="material-symbols-outlined text-3xl font-black relative z-10">music_note</span>
          </div>
        );
      case 'facebook':
        return (
          <div className="size-14 rounded-2xl bg-[#1877f2] flex items-center justify-center text-white shadow-[0_0_25px_rgba(24,119,242,0.3)]">
            <span className="material-symbols-outlined text-3xl font-black">facebook</span>
          </div>
        );
      default:
        return (
          <div className="size-14 rounded-2xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-orange-500">
            <span className="material-symbols-outlined text-3xl font-black">open_in_browser</span>
          </div>
        );
    }
  };

  const getAppName = () => {
    switch (sourceApp) {
      case 'instagram': return 'Instagram';
      case 'tiktok': return 'TikTok';
      case 'facebook': return 'Facebook';
      default: return 'Redes Sociales';
    }
  };

  return (
    <div className="fixed inset-0 z-[999999] bg-[#050505]/98 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-white overflow-hidden animate-[fadeIn_0.3s_ease-out]">
      {/* Background Soft Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-primary/10 blur-[90px] pointer-events-none"></div>

      {/* Main Container Card */}
      <div className="relative w-full max-w-md bg-[#121214]/90 border border-white/10 p-8 rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.8)] flex flex-col gap-6 text-center backdrop-blur-2xl">
        
        {/* Header App Info */}
        <div className="flex flex-col items-center gap-3">
          {getAppIcon()}
          <div>
            <span className="text-[9px] font-black text-primary uppercase tracking-[0.4em] block mb-1">
              Detector de {getAppName()}
            </span>
            <h2 className="text-xl font-black text-white uppercase tracking-tight leading-tight">
              Abre en tu Navegador
            </h2>
          </div>
        </div>

        {/* Informative Text */}
        <p className="text-xs text-text-secondary leading-relaxed px-2">
          Estás abriendo la aplicación dentro de la ventana de {getAppName()}. Para poder instalar <b>Aura Method</b> en tu pantalla de inicio y realizar el registro de forma segura, necesitas abrir el enlace en tu navegador.
        </p>

        {/* Bullet Steps with dynamic OS Instructions */}
        <div className="bg-white/5 border border-white/5 rounded-3xl p-5 text-left space-y-4 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
          <div className="flex items-start gap-4">
            <span className="bg-primary text-black font-black size-6 rounded-xl flex items-center justify-center text-xs shrink-0 shadow-[0_0_15px_rgba(242,127,13,0.3)]">
              1
            </span>
            <p className="text-xs text-white/90 leading-tight pt-0.5">
              Pulsa en los <b>{sourceApp === 'tiktok' ? 'tres puntos (...) o Compartir' : 'tres puntos (...)'}</b> situados en la esquina {sourceApp === 'tiktok' ? 'derecha' : 'superior derecha'}.
            </p>
          </div>
          
          <div className="flex items-start gap-4">
            <span className="bg-primary text-black font-black size-6 rounded-xl flex items-center justify-center text-xs shrink-0 shadow-[0_0_15px_rgba(242,127,13,0.3)]">
              2
            </span>
            <p className="text-xs text-white/90 leading-tight pt-0.5">
              {isIOS ? (
                <>Selecciona la opción <b>{sourceApp === 'instagram' ? '"Abrir en navegador externo"' : '"Abrir en Safari"'}</b>.</>
              ) : (
                <>Selecciona <b>"Abrir en Chrome"</b> o <b>"Abrir en el navegador"</b>.</>
              )}
            </p>
          </div>
        </div>

        {/* Visual Cue Alert */}
        <div className="flex items-center justify-center gap-2 text-primary/70 animate-pulse text-[10px] uppercase font-bold tracking-widest">
          <span className="material-symbols-outlined text-sm font-black">arrow_upward</span>
          <span>Pulsa el menú de arriba para salir</span>
          <span className="material-symbols-outlined text-sm font-black">arrow_upward</span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
